// ════════════════════════════════════════════════════════════════════════════
// M1.5 Madurez — definición de los valores del modelo VALOR·IA 5D (prompt 0732)
// Fuente única de verdad de los DESCRIPTORES de nivel: 6 niveles generales +
// matriz 5 dimensiones × 6 niveles. Anclada a la rúbrica v0.1 (umbrales por
// sub-indicador) de M1.5-metodologia-5D-subindicadores.md. Contenido metodológico
// (ES) — la consume el heatmap (tooltips + leyenda). Espejo del doc de metodología.
// ════════════════════════════════════════════════════════════════════════════
import type { DimensionMadurez, NivelMadurez } from '../../../shared/services/api';

export interface NivelInfo {
  nivel: NivelMadurez;
  /** Etiqueta corta M-0…M-5 para mostrar. */
  etiqueta: string;
  nombre: string;
  /** Significado general (independiente de dimensión). */
  resumen: string;
  /** Banda de color del modelo: 🟢 M-4/M-5 · 🟡 M-2/M-3 · 🔴 M-0/M-1. */
  color: 'VERDE' | 'AMARILLO' | 'ROJO';
}

/** Los 6 niveles VALOR·IA con su nombre y significado de negocio (eslabón débil = MIN). */
export const NIVELES_5D: NivelInfo[] = [
  {
    nivel: 'M_0',
    etiqueta: 'M-0',
    nombre: 'Errático / Sin medir',
    resumen:
      'No hay base estructural fiable, o la dimensión no tiene dato suficiente para evaluarse.',
    color: 'ROJO',
  },
  {
    nivel: 'M_1',
    etiqueta: 'M-1',
    nombre: 'Inicial',
    resumen: 'La estructura existe pero es informal: roles y responsabilidades sin formalizar.',
    color: 'ROJO',
  },
  {
    nivel: 'M_2',
    etiqueta: 'M-2',
    nombre: 'Documentada',
    resumen: 'Estructura formalizada: organigrama y catálogo de puestos existen y son accesibles.',
    color: 'AMARILLO',
  },
  {
    nivel: 'M_3',
    etiqueta: 'M-3',
    nombre: 'Gobernada',
    resumen:
      'Gobernanza activa: decisiones con responsable, red de roles de gobernanza y órganos operativos.',
    color: 'AMARILLO',
  },
  {
    nivel: 'M_4',
    etiqueta: 'M-4',
    nombre: 'Alineada',
    resumen:
      'La estructura sirve a la estrategia: KPIs con meta ligados a objetivos y cambios por workflow.',
    color: 'VERDE',
  },
  {
    nivel: 'M_5',
    etiqueta: 'M-5',
    nombre: 'Adaptativa',
    resumen:
      'Evoluciona de forma controlada y medida: escenarios what-if recurrentes y time-to-deploy optimizado.',
    color: 'VERDE',
  },
];

export const NIVEL_INFO: Record<NivelMadurez, NivelInfo> = Object.fromEntries(
  NIVELES_5D.map((n) => [n.nivel, n]),
) as Record<NivelMadurez, NivelInfo>;

/**
 * Marco teórico que sustenta cada dimensión (0736). El material académico de
 * ESTRUCTURA enriquece las 5D VALOR·IA; no las sustituye. El método del eslabón
 * débil (MIN) + semáforo se hereda de Hammer (PEMM) solo como MÉTODO, aplicado a
 * estructura — nunca el modelo PEMM de procesos (eso es M2.7).
 */
export const MARCO_TEORICO: Record<DimensionMadurez, string> = {
  FORMALIZACION:
    'Mintzberg (1979): estandarización y formalización del comportamiento · Malone & Crowston: teoría de la coordinación.',
  GOBERNABILIDAD: 'Galbraith — Star Model: procesos de decisión, poder y mecanismos de gobierno.',
  ALINEACION_ESTRATEGICA:
    'Galbraith — Star Model: ajuste estrategia↔estructura (cadena objetivo→KPI→puesto).',
  CAPACIDAD_CONTROL:
    'Mintzberg: ápice estratégico/línea media y span of control · Ostroff: organización horizontal.',
  CAPACIDAD_EVOLUCION:
    'Mintzberg: configuraciones organizativas · Lewin/Kotter: gestión del cambio estructural.',
};

/** Nota de método (común a las 5 dimensiones). */
export const METODO_ESLABON_DEBIL =
  'Nivel global = eslabón débil (MIN de las 5 dimensiones) + semáforo: método heredado de Hammer (PEMM) y aplicado a las 5 dimensiones VALOR·IA de estructura — no al modelo PEMM de procesos.';

/** La pregunta de gobernanza que evalúa cada dimensión (cabecera del tooltip). */
export const PREGUNTA_DIMENSION: Record<DimensionMadurez, string> = {
  FORMALIZACION: '¿Está la estructura documentada y formalizada?',
  GOBERNABILIDAD: '¿Hay una red de gobernanza densa con responsables claros?',
  ALINEACION_ESTRATEGICA: '¿La estructura sirve a la estrategia y al mapa de procesos?',
  CAPACIDAD_CONTROL: '¿Permite controlar la operación sin cuellos de botella?',
  CAPACIDAD_EVOLUCION: '¿Puede cambiar de forma controlada?',
};

/**
 * Matriz 5×6: qué significa cada nivel EN CADA dimensión, en lenguaje de negocio,
 * anclada a los umbrales de la rúbrica (% fichas, densidad de gobernanza, etc.).
 * M_0 = sin dato suficiente o por debajo del mínimo medible.
 */
export const DESCRIPTORES: Record<DimensionMadurez, Record<NivelMadurez, string>> = {
  FORMALIZACION: {
    M_0: 'Sin dato fiable de formalización.',
    M_1: 'Estructura informal: menos del 20% de los puestos tiene ficha documentada.',
    M_2: 'Documentación básica: organigrama y catálogo existen; al menos el 20% de puestos con ficha.',
    M_3: 'Documentación extendida: al menos la mitad de los puestos con ficha y ocupación cubierta.',
    M_4: 'Formalización sólida: la gran mayoría de puestos (≥80%) documentados y asignados.',
    M_5: 'Formalización total: prácticamente todos los puestos (≥95%) con ficha, en fuente única versionada.',
  },
  GOBERNABILIDAD: {
    M_0: 'Sin dato fiable de gobernanza.',
    M_1: 'Sin gobernanza: no hay roles de gobernanza ni órganos de gobierno definidos.',
    M_2: 'Gobernanza incipiente: existe al menos un órgano de gobierno; pocos roles de gobernanza.',
    M_3: 'Gobernanza activa: ~1 rol de gobernanza por persona; las decisiones tienen responsable.',
    M_4: 'Gobernanza densa: ≥2 roles de gobernanza por persona y órganos operativos.',
    M_5: 'Gobernanza madura: red densa (≥3 roles/persona), delegación y órganos consolidados.',
  },
  ALINEACION_ESTRATEGICA: {
    M_0: 'Sin dato fiable de alineación.',
    M_1: 'Sin alineación: procesos sin responsable funcional ni KPIs con meta.',
    M_2: 'Alineación inicial: algunos procesos cubiertos por roles o KPIs (≥20%).',
    M_3: 'Alineación parcial: la mitad de los procesos con responsable y meta de KPI (≥50%).',
    M_4: 'Alineación sólida: la mayoría de los procesos (≥80%) con KPIs ligados a objetivos.',
    M_5: 'Alineación total: prácticamente todo el mapa de procesos (≥95%) con metas y responsables.',
  },
  CAPACIDAD_CONTROL: {
    M_0: 'Sin dato fiable de control.',
    M_1: 'Sin control: jerarquía sin definir, o spans/niveles fuera de un rango sano.',
    M_2: 'Control básico: jerarquía formada, pero spans o número de niveles aún subóptimos.',
    M_3: 'Control razonable: estructura jerárquica con spans y profundidad en rango medio.',
    M_4: 'Control eficiente: spans equilibrados (3-8) y pocas capas (≤5 niveles).',
    M_5: 'Control óptimo: spans ideales (4-6), 2-4 niveles y sin cuellos de botella decisionales.',
  },
  CAPACIDAD_EVOLUCION: {
    M_0: 'Sin dato fiable de evolución.',
    M_1: 'Estructura estática: sin versionado del organigrama ni simulación de cambios.',
    M_2: 'Cambios trazados: el organigrama se versiona, pero sin simulación previa.',
    M_3: 'Cambios gestionados: versionado del organigrama más algún escenario what-if.',
    M_4: 'Evolución controlada: cambios vía workflow y escenarios what-if recurrentes.',
    M_5: 'Evolución adaptativa: what-if recurrentes y time-to-deploy de cambios medido y optimizado.',
  },
};

/** Descriptor de una celda (dimensión × nivel). */
export function descriptorCelda(dimension: DimensionMadurez, nivel: NivelMadurez): string {
  return DESCRIPTORES[dimension]?.[nivel] ?? '';
}

/**
 * Ancla conductual de una opción 0-4 del cuestionario (0738). La escala de percepción
 * 0-4 mapea a M-1..M-5 (opción n → nivel M_(n+1)); reutiliza la matriz de descriptores
 * 5×6 validada (0732) para dar significado observable a cada punto de la escala, en vez
 * de un número desnudo. Anclaje: Mintzberg/Ostroff vía los descriptores de dimensión.
 */
export function anclaOpcion(dimension: DimensionMadurez, opcion: number): string {
  const n = Math.max(0, Math.min(4, Math.round(opcion)));
  return descriptorCelda(dimension, `M_${n + 1}` as NivelMadurez);
}
